import React, { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { AccountingDepositEntity } from '../../../../../../api/types/accounting/accounting-deposit-entity';
import { updateDepositDateOperation } from '../operations/update-deposit-date-operation';

interface EditDepositDateDialogProps {
  visible: boolean;
  depositId: number | null;
  /** Current date shown in the parent dialog — used to seed the calendar. */
  currentDate: Date | null;
  onHide: () => void;
  /** Called after a successful update with the freshly-updated deposit. */
  onSuccess: (updated: AccountingDepositEntity) => void;
}

export const EditDepositDateDialog: React.FC<EditDepositDateDialogProps> = ({
  visible,
  depositId,
  currentDate,
  onHide,
  onSuccess,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Seed the calendar with the current deposit date every time the dialog
  // (re)opens, so reopening for a different deposit doesn't carry over the
  // previous selection.
  useEffect(() => {
    if (!visible) return;
    setSelectedDate(currentDate ? new Date(currentDate) : null);
  }, [visible, currentDate]);

  const handleClose = () => {
    if (loading) return;
    onHide();
  };

  const handleApprove = async () => {
    if (!depositId || !selectedDate || loading) return;
    await updateDepositDateOperation(
      depositId,
      selectedDate,
      setLoading,
      (updated) => {
        onSuccess(updated);
        onHide();
      },
    );
  };

  const footer = (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
      <Button
        label="ביטול"
        icon="pi pi-times"
        severity="secondary"
        text
        onClick={handleClose}
        disabled={loading}
      />
      <Button
        label={loading ? 'שומר...' : 'אישור'}
        icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
        onClick={handleApprove}
        disabled={!depositId || !selectedDate || loading}
      />
    </div>
  );

  return (
    <Dialog
      header="עריכת תאריך הפקדה"
      visible={visible}
      onHide={handleClose}
      modal
      draggable={false}
      resizable={false}
      closable={!loading}
      style={{ width: '420px', direction: 'rtl' }}
      footer={footer}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
          padding: '0.25rem 0',
        }}
      >
        <label style={{ fontWeight: 600, fontSize: '0.9rem' }}>
          תאריך הפקדה חדש *
        </label>
        <Calendar
          value={selectedDate}
          onChange={(e) => setSelectedDate((e.value as Date) ?? null)}
          dateFormat="dd/mm/yy"
          showIcon
          showButtonBar
          placeholder="בחר תאריך"
          disabled={loading}
          style={{ width: '100%', direction: 'ltr' }}
          inputStyle={{ direction: 'rtl', width: '100%' }}
        />
      </div>
    </Dialog>
  );
};
